import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Embed } from '@clds/icon';
import styled from 'styled-components';
import Popover from '@clds/popover';
import { Checkbox } from '@clds/checkbox';
import { InlineFieldLayout } from '@clds/form';
import { EnvironmentData } from '@cld/unicorn-api';
import { ConsoleLogger, ConsoleLoggerCategory, consoleLoggerCategories } from '../../state/types';

export const unicornDevToolsContainerId = 'unicorn-dev-tools';

interface DevModeToolsProps {
  environmentData: EnvironmentData;
  enabledLoggerCategories: ConsoleLogger['enabledCategories'];
  onLoggerCategoryToggle: ConsoleLogger['toggleCategory'];
  setForceSplitError: (newValue: boolean) => void;
  forceSplitError: boolean;
}

const DebugButton = styled.button`
  align-items: center;
  background-color: rgba(243, 156, 18, 0.4);
  border: none;
  display: flex;
  height: 40px;
  justify-content: center;
  outline: none;
  transition: background-color 0.3s;
  width: 40px;

  &:hover {
    background-color: rgba(243, 156, 18, 0.7);
    cursor: pointer;
  }

  svg {
    fill: rgb(192, 57, 43);
  }
`;

const Heading = styled.h2`
  border-bottom: 1px solid black;
  padding-bottom: 10px;
  margin: 0 0 22px 0;
`;

const LoggerCategoryLabel = styled.div`
  margin-left: 4px;
`;

const LoggerCategoryLayout = styled(InlineFieldLayout)`
  &:not(:first-of-type) {
    margin-left: 18px;
  }
`;

const LogLevelsList = styled.div`
  display: flex;
`;

const SplitSection = styled.p`
  align-items: center;
  display: flex;
`;

const SplitCheckbox = styled(Checkbox)`
  margin-left: 8px;
`;

export const DevModeTools = ({
  environmentData,
  enabledLoggerCategories,
  onLoggerCategoryToggle,
  forceSplitError,
  setForceSplitError,
}: DevModeToolsProps) => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [activeLoggerCategories, setActiveLoggerCategories] = useState<ConsoleLoggerCategory[]>([...enabledLoggerCategories]);

  useEffect(() => {
    void waitFor(unicornDevToolsContainerId).then((foundElement) => {
      setElement(foundElement);
    });
  });

  if (!element) {
    return null;
  }

  return ReactDOM.createPortal(
    <Popover
      overlay={
        <div>
          <Heading>Unicorn DevTools</Heading>

          <p>
            <strong>Environment:</strong> {environmentData.name}
          </p>

          <p>
            <strong>Console backend URL:</strong> {environmentData.backendUrls.console}
          </p>

          <SplitSection>
            <strong>Force split error:</strong>

            <SplitCheckbox
              onChange={() => {
                setForceSplitError(!forceSplitError);
              }}
              value={forceSplitError}
            />
          </SplitSection>

          <p>
            <strong>Logger debug categories:</strong>
          </p>

          <LogLevelsList>
            {consoleLoggerCategories.map((category) => (
              <LoggerCategoryLayout key={category} label={<LoggerCategoryLabel>{category}</LoggerCategoryLabel>}>
                <Checkbox
                  onChange={() => {
                    onLoggerCategoryToggle(category);
                    setActiveLoggerCategories([...enabledLoggerCategories]);
                  }}
                  value={activeLoggerCategories.includes(category)}
                />
              </LoggerCategoryLayout>
            ))}
          </LogLevelsList>
        </div>
      }
      trigger="hover"
      // visible={true}
      placement="right"
    >
      <DebugButton>
        <Embed size="lg" />
      </DebugButton>
    </Popover>,
    element
  );
};

function waitFor(id: string) {
  return new Promise<HTMLElement>((resolve) => {
    const element = document.getElementById(id);

    if (element) {
      return resolve(element);
    }

    const observer = new MutationObserver(() => {
      const appearedElement = document.getElementById(id);

      if (appearedElement) {
        observer.disconnect();
        resolve(appearedElement);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });
}
